// Wandelingen: overzichtslijst met filters + detailpagina.
// Filters (afstand, moeilijkheid, regio, sort) zijn module-lokaal.

import { setTitle, navigate } from '../main.js'
import { esc } from '../util.js'
import { icon } from '../icons.js'
import { md } from '../markdown.js'
import { setFilter } from '../store.js'

// ---------- Module-lokale filterstaat ----------
let sortMode = 'sterren'       // 'sterren' | 'afstand'
let distanceFilter = 'alle'    // 'alle' | '<5' | '5-10' | '>10'
let difficultyFilter = 'alle'  // 'alle' | 'makkelijk' | 'middel' | 'zwaar'
let regionFilter = 'alle'      // 'alle' | regionId

// ---------- Helpers ----------

function formatDuration(min) {
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h === 0) return `${m} min`
  return m > 0 ? `${h}u${String(m).padStart(2, '0')}` : `${h}u`
}

function difficultyColor(d) {
  switch (d) {
    case 'makkelijk': return 'var(--warm)'
    case 'middel':    return 'var(--amber-600)'
    case 'zwaar':     return 'var(--accent)'
    default:          return 'var(--muted)'
  }
}

function difficultyLabel(d) {
  switch (d) {
    case 'makkelijk': return 'Makkelijk'
    case 'middel':    return 'Middel'
    case 'zwaar':     return 'Zwaar'
    default:          return d
  }
}

// ---------- List view ----------

export function renderHikes(root, ctx) {
  setTitle('Wandelingen')
  const hikes = ctx.data.hikes ?? []

  // Filteren
  let filtered = hikes

  if (distanceFilter === '<5') {
    filtered = filtered.filter(h => h.distanceKm < 5)
  } else if (distanceFilter === '5-10') {
    filtered = filtered.filter(h => h.distanceKm >= 5 && h.distanceKm <= 10)
  } else if (distanceFilter === '>10') {
    filtered = filtered.filter(h => h.distanceKm > 10)
  }

  if (difficultyFilter !== 'alle') {
    filtered = filtered.filter(h => h.difficulty === difficultyFilter)
  }

  if (regionFilter !== 'alle') {
    filtered = filtered.filter(h => h.regionId === regionFilter)
  }

  // Sorteren
  if (sortMode === 'sterren') {
    filtered = [...filtered].sort((a, b) => b.stars - a.stars || a.distanceKm - b.distanceKm)
  } else {
    filtered = [...filtered].sort((a, b) => a.distanceKm - b.distanceKm)
  }

  // Unieke regio's voor het regio-filter
  const regionIds = [...new Set(hikes.map(h => h.regionId).filter(Boolean))]
  const regions = regionIds
    .map(id => ({ id, name: ctx.indexes.nodesById.get(id)?.name ?? id }))
    .sort((a, b) => a.name.localeCompare(b.name, 'nl'))

  // Kaarten bouwen
  const cards = filtered.map(h => {
    const regionName = h.regionId ? ctx.indexes.nodesById.get(h.regionId)?.name : null
    return `
      <a class="hike-card card" href="#/hikes/${esc(h.id)}">
        <div class="hike-header">
          <h2 class="hike-name">${esc(h.name)}</h2>
          <div class="hike-stars">${'★'.repeat(h.stars)}${'☆'.repeat(5 - h.stars)}</div>
        </div>
        <div class="hike-stats">
          <span class="hike-stat">${icon('walk')} ${h.distanceKm} km</span>
          ${h.ascentM != null ? `<span class="hike-stat">↑ ${h.ascentM} m</span>` : ''}
          ${h.durationMin != null ? `<span class="hike-stat">${formatDuration(h.durationMin)}</span>` : ''}
          <span class="hike-stat">${h.loop ? icon('rotate') + ' Rondwandeling' : '→ Heen en terug'}</span>
        </div>
        <div class="badge-row">
          <span class="badge difficulty-${esc(h.difficulty)}" style="background:${difficultyColor(h.difficulty)};color:#fff">${difficultyLabel(h.difficulty)}</span>
          ${regionName ? `<span class="badge">${esc(regionName)}</span>` : ''}
        </div>
        <p class="hike-blurb">${esc(h.blurb)}</p>
      </a>`
  }).join('')

  const body = filtered.length
    ? cards
    : '<p class="empty">Geen wandelingen gevonden met deze filters.</p>'

  root.innerHTML = `
    <div class="page">
      <h1 class="title">Wandelingen</h1>
      <p class="subtitle">Wandelroutes in de regio</p>

      <div class="sort-toggle" role="group" aria-label="Sorteer">
        <button class="chip toggle ${sortMode === 'sterren' ? 'active' : ''}" data-sort="sterren">Sterren</button>
        <button class="chip toggle ${sortMode === 'afstand' ? 'active' : ''}" data-sort="afstand">Afstand</button>
      </div>

      <div class="filter-group" role="group" aria-label="Afstandsfilter">
        <button class="chip toggle ${distanceFilter === 'alle' ? 'active' : ''}" data-dist="alle">Alle</button>
        <button class="chip toggle ${distanceFilter === '<5' ? 'active' : ''}" data-dist="<5">&lt; 5 km</button>
        <button class="chip toggle ${distanceFilter === '5-10' ? 'active' : ''}" data-dist="5-10">5–10 km</button>
        <button class="chip toggle ${distanceFilter === '>10' ? 'active' : ''}" data-dist=">10">&gt; 10 km</button>
      </div>

      <div class="filter-group" role="group" aria-label="Moeilijkheidsfilter">
        <button class="chip toggle ${difficultyFilter === 'alle' ? 'active' : ''}" data-diff="alle">Alle</button>
        <button class="chip toggle ${difficultyFilter === 'makkelijk' ? 'active' : ''}" data-diff="makkelijk" style="border-color:var(--warm)">Makkelijk</button>
        <button class="chip toggle ${difficultyFilter === 'middel' ? 'active' : ''}" data-diff="middel" style="border-color:var(--amber-600)">Middel</button>
        <button class="chip toggle ${difficultyFilter === 'zwaar' ? 'active' : ''}" data-diff="zwaar" style="border-color:var(--accent)">Zwaar</button>
      </div>

      ${regions.length ? `
        <div class="filter-group" role="group" aria-label="Regiofilter">
          <button class="chip toggle ${regionFilter === 'alle' ? 'active' : ''}" data-region="alle">Alle</button>
          ${regions.map(r => `<button class="chip toggle ${regionFilter === r.id ? 'active' : ''}" data-region="${esc(r.id)}">${esc(r.name)}</button>`).join('')}
        </div>
      ` : ''}

      <div class="hike-list">
        ${body}
      </div>
    </div>
  `

  // Wire sort toggle
  root.querySelectorAll('.sort-toggle [data-sort]').forEach(b => {
    b.addEventListener('click', () => {
      sortMode = b.dataset.sort
      renderHikes(root, ctx)
    })
  })

  // Wire distance filter
  root.querySelectorAll('[data-dist]').forEach(b => {
    b.addEventListener('click', () => {
      distanceFilter = b.dataset.dist
      renderHikes(root, ctx)
    })
  })

  // Wire difficulty filter
  root.querySelectorAll('[data-diff]').forEach(b => {
    b.addEventListener('click', () => {
      difficultyFilter = b.dataset.diff
      renderHikes(root, ctx)
    })
  })

  // Wire region filter
  root.querySelectorAll('[data-region]').forEach(b => {
    b.addEventListener('click', () => {
      regionFilter = b.dataset.region
      renderHikes(root, ctx)
    })
  })
}

// ---------- Detail view ----------

function renderSection(s) {
  const title = s.title || 'Beschrijving'
  const body = `<div class="section-body">${md(s.body)}</div>`
  if (s.depth === 'deep') {
    return `<details class="deep"><summary>${esc(title)}</summary>${body}</details>`
  }
  return `<div class="card"><h2>${esc(title)}</h2>${body}</div>`
}

function renderLinkedNodes(hike, ctx) {
  const seen = new Set()
  const nodes = []

  // nodeIds eerst
  for (const nid of hike.nodeIds ?? []) {
    const node = ctx.indexes.nodesById.get(nid)
    if (node && !seen.has(nid)) {
      seen.add(nid)
      nodes.push(node)
    }
  }

  // regionId toevoegen als het er nog niet in zit
  if (hike.regionId && !seen.has(hike.regionId)) {
    const node = ctx.indexes.nodesById.get(hike.regionId)
    if (node) nodes.push(node)
  }

  if (!nodes.length) return ''

  const items = nodes.map(n => `
    <a class="entity" href="#/node/${esc(n.id)}">
      <div class="icon-bubble">${icon('map')}</div>
      <div class="e-body">
        <div class="e-title">${esc(n.name)}</div>
        ${n.blurb ? `<div class="e-note">${esc(n.blurb).slice(0, 160)}</div>` : ''}
      </div>
    </a>`).join('')

  return `
    <div class="card">
      <h2>Gekoppelde plekken</h2>
      <div class="entity-list">${items}</div>
    </div>`
}

export function renderHike(root, ctx, id) {
  const hike = ctx.indexes.hikesById.get(id)
  if (!hike) {
    setTitle('Wandeling')
    root.innerHTML = `<div class="page"><p class="empty">Onbekende wandeling: ${esc(id)}</p></div>`
    return
  }
  setTitle(hike.name)

  const regionName = hike.regionId ? ctx.indexes.nodesById.get(hike.regionId)?.name : null

  const statsItems = [
    `<div class="stat-item">
      <div class="stat-value">${hike.distanceKm} km</div>
      <div class="stat-label">Afstand</div>
    </div>`,
  ]

  if (hike.ascentM != null) {
    statsItems.push(`
      <div class="stat-item">
        <div class="stat-value">↑ ${hike.ascentM} m</div>
        <div class="stat-label">Stijging</div>
      </div>`)
  }

  if (hike.durationMin != null) {
    statsItems.push(`
      <div class="stat-item">
        <div class="stat-value">${formatDuration(hike.durationMin)}</div>
        <div class="stat-label">Duur</div>
      </div>`)
  }

  statsItems.push(`
    <div class="stat-item">
      <div class="stat-value"><span class="badge" style="background:${difficultyColor(hike.difficulty)};color:#fff;margin:0">${difficultyLabel(hike.difficulty)}</span></div>
      <div class="stat-label">Niveau</div>
    </div>`,
  `<div class="stat-item">
      <div class="stat-value">${hike.loop ? 'Rondwandeling' : 'Heen &amp; terug'}</div>
      <div class="stat-label">Type</div>
    </div>`)

  const tags = (hike.tags ?? []).map(t => `<span class="badge">${esc(t)}</span>`).join('')

  const sections = (hike.sections ?? []).map(s => renderSection(s)).join('')

  root.innerHTML = `
    <div class="page">
      <div class="crumbs"><a href="#/hikes">‹ Wandelingen</a></div>
      <h1 class="title">${esc(hike.name)}</h1>

      <div class="hike-detail-stats">
        ${statsItems.join('')}
      </div>

      <div class="badge-row">
        <span class="hike-stars">${'★'.repeat(hike.stars)}${'☆'.repeat(5 - hike.stars)}</span>
        ${tags}
      </div>

      <p class="hike-intro">${esc(hike.blurb)}</p>

      ${sections}

      <div class="card hike-track-placeholder">
        <h2>Route</h2>
        <p class="e-meta">Route-lijn wordt later toegevoegd.</p>
        <div class="btn-row">
          <a class="btn secondary" href="https://www.google.com/maps/dir/?api=1&destination=${hike.coord.lat},${hike.coord.lng}&travelmode=walking" target="_blank" rel="noopener">
            ${icon('walk')} Open startpunt in kaarten
          </a>
        </div>
      </div>

      ${renderLinkedNodes(hike, ctx)}

      <div class="card">
        <h2>Bron</h2>
        <ul class="link-list">
          <li><a href="${esc(hike.source.url)}" target="_blank" rel="noopener">${icon('external-link')} ${esc(hike.source.title)}</a></li>
        </ul>
      </div>

      <div class="card">
        <div class="btn-row">
          <button class="btn" id="show-on-map">${icon('map')} Toon op kaart</button>
        </div>
      </div>
    </div>
  `

  // Wire "Toon op kaart" button
  root.querySelector('#show-on-map')?.addEventListener('click', () => {
    setFilter('showHikes', true)
    navigate('#/map')
    setTimeout(() => {
      const event = new CustomEvent('center-hike', { detail: { coord: hike.coord, id: hike.id } })
      window.dispatchEvent(event)
    }, 300)
  })
}
